import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { extendQuotationValidityApi } from "./quotation-api";
import { sanitizeInput } from "../common/security-utils";

const ExtendValidityModal = ({ isOpen, quotation, onClose, onSuccess }) => {
  const [validDate, setValidDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});

  // Minimum allowed date is today
  const todayStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (isOpen) {
      // Default new validity date to 7 days from today
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 7);
      setValidDate(defaultDate.toISOString().split("T")[0]);
      setRemarks("");
      setErrors({});
      setApiError("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const errs = {};
    if (!validDate) {
      errs.validDate = "Please select a valid expiry date.";
    } else if (validDate < todayStr) {
      errs.validDate = "Expiry date cannot be in the past.";
    }

    if (!remarks || !remarks.trim()) {
      errs.remarks = "Remarks is mandatory.";
    } else if (remarks.trim().length < 3) {
      errs.remarks = "Remarks must be at least 3 characters long.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError("");

    try {
      const payload = {
        valid_date: validDate,
        remarks: sanitizeInput(remarks.trim()),
      };

      const response = await extendQuotationValidityApi(quotation?.id || quotation?.uuid, payload);
      const resData = response?.data ?? response;

      if (resData?.success === true || response?.status === 200) {
        if (onSuccess) {
          onSuccess(resData);
        }
      } else {
        setApiError(resData?.message || "Failed to extend quotation validity. Please try again.");
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred while extending quotation validity.";
      setApiError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? dateStr
        : d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
    } catch {
      return dateStr;
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={isSubmitting ? undefined : onClose}
      centered
      backdrop="static"
      keyboard={!isSubmitting}
      contentClassName="border-0 shadow"
      style={{ fontFamily: "'Euclid Circular B', sans-serif" }}
    >
      {/* Header */}
      <Modal.Header
        style={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #EAEAEA",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div className="fs-16 fw-600 text-dkblue">Extend Quotation Validity</div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          style={{
            background: "none",
            border: "none",
            color: "#6c757d",
            fontSize: "20px",
            lineHeight: "1",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            padding: "0 4px",
          }}
          aria-label="Close"
        >
          &times;
        </button>
      </Modal.Header>

      {/* Body */}
      <Modal.Body style={{ padding: "20px 24px" }}>
        {apiError && (
          <Alert variant="danger" className="py-2 px-3 fs-13 mb-3">
            {apiError}
          </Alert>
        )}

        {/* Info Section */}
        <div
          style={{
            backgroundColor: "#F4F6F8",
            border: "1px solid #E5E9EB",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <div>
            <div className="fs-12 fw-500 text-grey">Quotation ID</div>
            <div className="fs-14 fw-600 text-dkblue">
              {quotation?.quotation_id || "--"}
            </div>
          </div>
          <div>
            <div className="fs-12 fw-500 text-grey">Current Expiry Date</div>
            <div className="fs-14 fw-600 text-dkblue">
              {formatDisplayDate(quotation?.valid_date)}
            </div>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          {/* New Expiry Date Field */}
          <Form.Group className="mb-3">
            <div className="fs-13 fw-500 text-dark mb-1">
              New Expiry Date<span className="text-danger">*</span>
            </div>
            <Form.Control
              type="date"
              min={todayStr}
              value={validDate}
              onChange={(e) => {
                setValidDate(e.target.value);
                if (errors.validDate) {
                  setErrors((prev) => ({ ...prev, validDate: "" }));
                }
              }}
              isInvalid={Boolean(errors.validDate)}
              style={{
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "14px",
                borderColor: errors.validDate ? "#dc3545" : "#D4DDE7",
              }}
            />
            {errors.validDate && (
              <Form.Control.Feedback type="invalid" style={{ fontSize: "12px" }}>
                {errors.validDate}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Remarks Field */}
          <Form.Group className="mb-2">
            <div className="fs-13 fw-500 text-dark mb-1">
              Remarks<span className="text-danger">*</span>
            </div>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter remarks for extending validity..."
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                if (errors.remarks) {
                  setErrors((prev) => ({ ...prev, remarks: "" }));
                }
              }}
              isInvalid={Boolean(errors.remarks)}
              maxLength={500}
              style={{
                borderRadius: "8px",
                padding: "10px 12px",
                fontSize: "14px",
                resize: "vertical",
                borderColor: errors.remarks ? "#dc3545" : "#D4DDE7",
              }}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              {errors.remarks ? (
                <span className="text-danger" style={{ fontSize: "12px" }}>
                  {errors.remarks}
                </span>
              ) : (
                <span></span>
              )}
              <span className="text-grey" style={{ fontSize: "12px" }}>
                {remarks.length}/500
              </span>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer
        style={{
          borderTop: "1px solid #EAEAEA",
          padding: "14px 24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
        }}
      >
        <Button
          variant="light"
          onClick={onClose}
          disabled={isSubmitting}
          style={{
            backgroundColor: "#FFFFFF",
            color: "#0E1B2E",
            border: "1px solid #D4DDE7",
            borderRadius: "100px",
            fontWeight: 600,
            fontSize: "14px",
            padding: "8px 24px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            backgroundColor: "#3D4AF5",
            borderColor: "#3D4AF5",
            color: "#FFFFFF",
            borderRadius: "100px",
            fontWeight: 600,
            fontSize: "14px",
            padding: "8px 24px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" />
              <span>Extending...</span>
            </>
          ) : (
            "Extend Validity"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExtendValidityModal;
